import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner, Table } from "reactstrap";
import Select from "react-select";

import { coberturaAdjudicacao, criarAdjudicacao, encerrarAdjudicacao, excluirAdjudicacao, impactoAdjudicacao, listarAdjudicacoes, rankingBid } from "services/bidFreteApi";
import { makeReactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import {
  FONTE_UI,
  JUSTIFICATIVA_MIN,
  PAPEL_UI,
  REGIOES,
  UFS_POR_REGIAO,
  chaveCandidata,
  economiaPct,
  erroDaApi,
  fmtBRL,
  fmtDataISO,
  fmtNumero,
  fmtPct,
  fmtSiglas,
  hojeISO,
  listaSiglas,
  montarAdjudicacao,
  nomeCandidata,
  precisaJustificarEmAreas,
  situacaoCobertura,
  statusDaEtapa,
  ufsDaArea,
  ufsDoBid,
  unirCandidatas,
  validarAdjudicacao,
} from "../regras";
import MapaCoberturaUfs from "./MapaCoberturaUfs";

// Dentro de modal: o menu vai para o body (portal) e precisa ficar acima do
// .modal (z-index 1050), senão abre por trás e só dá para digitar.
const selectNoModalStyles = makeReactSelectStyles({ portalZIndex: 1070 });

// Aba Vencedoras (H9 PO1-2849, só RFP; chamada de Adjudicação até 18/10/2026): mapa das UFs do escopo com e sem
// vencedora (GET /cobertura), inclusão de vencedora por área (Brasil, região
// ou lista de UFs) a partir do ranking daquela área, com papel PRINCIPAL ou
// SECUNDARIA, percentual opcional, vigência e justificativa obrigatória fora do
// 1º lugar; impacto nas tabelas da incumbente (GET /impacto?ufs=, só leitura);
// excluir; encerrar só com cobertura completa (toda UF com vencedora e
// principal, soma de percentuais até 100).

const NIVEIS = [
  { value: "BRASIL", label: "Brasil" },
  { value: "REGIAO", label: "Região" },
  { value: "UF", label: "Lista de UFs" },
];

const ESTADO_UF = {
  ok: "com vencedora",
  sem_vencedora: "sem vencedora",
  sem_principal: "sem principal",
  acima_100: "percentual acima de 100%",
};

function umAno(de) {
  const d = new Date(`${de}T00:00:00`);
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return hojeISO(d);
}

function formInicial() {
  const ini = hojeISO();
  return { nivel: "BRASIL", area: "", ufs: [], candidata: null, papel: "PRINCIPAL", percentual: "", vigenciaIni: ini, vigenciaFim: umAno(ini), justificativa: "" };
}

export default function AbaAdjudicacao({ bid, empresaId, recarregar, onChanged }) {
  const ufsEscopo = useMemo(() => ufsDoBid(bid), [bid]);
  const regioesEscopo = useMemo(() => Object.keys(UFS_POR_REGIAO).filter((r) => UFS_POR_REGIAO[r].some((u) => ufsEscopo.indexOf(u) !== -1)), [ufsEscopo]);
  const rankingPronto = statusDaEtapa(bid, "RANKING") === "CONCLUIDA";
  const encerrada = statusDaEtapa(bid, "ADJUDICACAO") === "CONCLUIDA";

  const [adjudicacoes, setAdjudicacoes] = useState([]);
  const [cobertura, setCobertura] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [form, setForm] = useState(formInicial);
  const [erros, setErros] = useState({});
  const [rankings, setRankings] = useState([]);
  const [carregandoRanking, setCarregandoRanking] = useState(false);
  const [impacto, setImpacto] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmarEncerrar, setConfirmarEncerrar] = useState(false);

  const carregar = useCallback(async () => {
    if (!empresaId || !bid) return;
    setLoading(true);
    setErro(null);
    try {
      const [a, c] = await Promise.all([listarAdjudicacoes(empresaId, bid.id), coberturaAdjudicacao(empresaId, bid.id).catch(() => null)]);
      setAdjudicacoes(Array.isArray(a) ? a : (a && a.items) || []);
      setCobertura(c);
    } catch (err) {
      setErro(erroDaApi(err, "Não foi possível carregar as vencedoras.").mensagem);
    } finally {
      setLoading(false);
    }
  }, [empresaId, bid]);

  useEffect(() => {
    if (rankingPronto) carregar();
  }, [rankingPronto, carregar]);

  const ufsForm = useMemo(() => ufsDaArea(form.nivel, form.area, (form.ufs || []).map((o) => o.value), ufsEscopo), [form.nivel, form.area, form.ufs, ufsEscopo]);

  // Ranking da área escolhida: Brasil/Região = 1 consulta; lista de UFs = 1 por UF.
  const chaveArea = `${form.nivel}|${form.area}|${ufsForm.join(";")}`;
  useEffect(() => {
    if (!rankingPronto || !empresaId || !bid) return undefined;
    let consultas = [];
    if (form.nivel === "BRASIL") consultas = [{ nivel: "BRASIL", area: "BR" }];
    else if (form.nivel === "REGIAO" && form.area) consultas = [{ nivel: "REGIAO", area: form.area }];
    else if (form.nivel === "UF") consultas = ufsForm.map((u) => ({ nivel: "UF", area: u }));
    if (!consultas.length) {
      setRankings([]);
      return undefined;
    }
    let ativo = true;
    setCarregandoRanking(true);
    Promise.all(consultas.map((q) => rankingBid(empresaId, bid.id, q).catch(() => ({ candidatas: [] }))))
      .then((rs) => {
        if (ativo) setRankings(rs);
      })
      .finally(() => {
        if (ativo) setCarregandoRanking(false);
      });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveArea, rankingPronto, empresaId, bid]);

  // Impacto na incumbente para as UFs da área (só leitura).
  useEffect(() => {
    if (!rankingPronto || !empresaId || !bid || !ufsForm.length) {
      setImpacto(null);
      return undefined;
    }
    let ativo = true;
    impactoAdjudicacao(empresaId, bid.id, ufsForm)
      .then((r) => {
        if (ativo) setImpacto(r);
      })
      .catch(() => {
        if (ativo) setImpacto(null);
      });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveArea, rankingPronto, empresaId, bid]);

  const candidatas = useMemo(() => unirCandidatas(rankings), [rankings]);
  const precisa = form.candidata ? precisaJustificarEmAreas(rankings, form.candidata.idTransportadora) : false;
  const situacao = useMemo(() => situacaoCobertura(ufsEscopo, cobertura, adjudicacoes), [ufsEscopo, cobertura, adjudicacoes]);
  const vencedorasPorUf = useMemo(() => {
    const m = {};
    adjudicacoes.forEach((a) => {
      listaSiglas(a.ufs).forEach((u) => {
        m[u] = (m[u] || []).concat(nomeCandidata(a));
      });
    });
    return m;
  }, [adjudicacoes]);
  const tabelasImpacto = (impacto && (Array.isArray(impacto) ? impacto : impacto.tabelas || impacto.items)) || [];
  const mudar = (patch) => setForm((f) => ({ ...f, ...patch }));

  const executar = async (chave, fn, fallback, sucesso) => {
    if (busy) return false;
    setBusy(chave);
    setErro(null);
    setAviso(null);
    try {
      await fn();
      if (sucesso) setAviso(sucesso);
      await carregar();
      if (recarregar) recarregar();
      if (onChanged) onChanged();
      return true;
    } catch (err) {
      const e = erroDaApi(err, fallback);
      if (e.errors) setErros(e.errors);
      setErro(e.errors ? `${e.mensagem} ${Object.values(e.errors).join(" ")}` : e.mensagem);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const incluir = async () => {
    const e = validarAdjudicacao(form, { precisa, ufs: ufsForm });
    setErros(e);
    if (Object.keys(e).length) return;
    const ok = await executar("incluir", () => criarAdjudicacao(empresaId, bid.id, montarAdjudicacao(form, { ufs: ufsForm, precisa })), "Não foi possível incluir a vencedora.", "Vencedora incluída.");
    if (ok) setForm((f) => ({ ...formInicial(), nivel: f.nivel, area: f.area, ufs: f.ufs, vigenciaIni: f.vigenciaIni, vigenciaFim: f.vigenciaFim }));
  };

  if (!rankingPronto) return <div className="bf-placeholder">A escolha das vencedoras abre depois do ranking: rode a simulação com as propostas recebidas.</div>;

  const opcoesUf = ufsEscopo.map((u) => ({ value: u, label: u }));

  return (
    <>
      <div className="lf-section">
        <div className="lf-section-title">Cobertura das UFs do escopo</div>
        {loading && !adjudicacoes.length ? (
          <div className="lf-muted">
            <Spinner size="sm" /> Carregando...
          </div>
        ) : (
          <>
            <div className="bf-cobertura">
              <MapaCoberturaUfs ufs={situacao.ufs} rotulos={ESTADO_UF} vencedorasPorUf={vencedorasPorUf} />
              <div className="bf-cobertura-lado">
                <div className="lf-kpi">
                  <div className="lf-kpi-label">UFs com vencedora</div>
                  <div className="lf-kpi-value">
                    {situacao.ufs.length - situacao.semVencedora.length} de {situacao.ufs.length}
                  </div>
                </div>
                <ul className="bf-legenda-mapa">
                  <li><span className="bf-cor estado-ok" />Com vencedora</li>
                  <li><span className="bf-cor estado-sem_vencedora" />Sem vencedora</li>
                  <li><span className="bf-cor estado-sem_principal" />Sem principal ou acima de 100%</li>
                  <li><span className="bf-cor fora" />Fora do escopo</li>
                </ul>
                <div className="bf-legenda">
                  {situacao.semVencedora.length ? <span className="bf-text-danger">sem vencedora: {situacao.semVencedora.join(", ")}</span> : null}
                  {situacao.semPrincipal.length ? <span className="bf-text-warning">sem principal: {situacao.semPrincipal.join(", ")}</span> : null}
                  {situacao.acimaDe100.length ? <span className="bf-text-warning">percentual acima de 100%: {situacao.acimaDe100.join(", ")}</span> : null}
                  {situacao.abaixoDe100.length ? <span className="lf-muted">percentuais somam menos de 100% em: {situacao.abaixoDe100.join(", ")}</span> : null}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {erro ? <div className="lf-aviso lf-aviso-danger">{erro}</div> : null}
      {aviso ? <div className="lf-aviso lf-aviso-info">{aviso}</div> : null}

      {!encerrada ? (
        <div className="lf-section" data-testid="bf-form-adjudicacao">
          <div className="lf-section-title">Incluir vencedora</div>
          <div className="bf-tab-toolbar">
            <FormGroup>
              <Label>Área</Label>
              <Input
                type="select"
                bsSize="sm"
                value={form.nivel}
                aria-label="Nível da área"
                onChange={(e) => {
                  const v = e.target.value;
                  mudar({ nivel: v, area: v === "REGIAO" ? regioesEscopo[0] || "" : "", ufs: [], candidata: null });
                }}
              >
                {NIVEIS.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            {form.nivel === "REGIAO" ? (
              <FormGroup>
                <Label>Região</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={form.area}
                  aria-label="Região"
                  onChange={(e) => {
                    const v = e.target.value;
                    mudar({ area: v, candidata: null });
                  }}
                >
                  {regioesEscopo.map((r) => (
                    <option key={r} value={r}>
                      {REGIOES[r]}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            ) : null}
            {form.nivel === "UF" ? (
              <FormGroup style={{ minWidth: 260 }}>
                <Label>UFs</Label>
                <Select
                  isMulti
                  className="react-select info"
                  classNamePrefix="react-select"
                  options={opcoesUf}
                  value={form.ufs}
                  onChange={(v) => mudar({ ufs: v || [], candidata: null })}
                  styles={selectNoModalStyles}
                  placeholder="Escolha as UFs"
                  noOptionsMessage={() => "Sem opções"}
                  {...reactSelectPortalProps()}
                />
              </FormGroup>
            ) : null}
            <div className="lf-muted">UFs: {fmtSiglas(ufsForm)}</div>
          </div>
          {erros.area ? <div className="lf-erro-campo">{erros.area}</div> : null}

          <Label>Transportadora (do ranking da área)</Label>
          {carregandoRanking ? (
            <div className="lf-muted">
              <Spinner size="sm" /> Carregando o ranking...
            </div>
          ) : candidatas.length === 0 ? (
            <div className="lf-muted">Nenhuma candidata no ranking desta área.</div>
          ) : (
            <div className="bf-cand-opcoes" role="listbox">
              {candidatas.map((c, i) => {
                const sel = form.candidata && chaveCandidata(form.candidata) === chaveCandidata(c);
                const fonte = FONTE_UI[c.fonte] || FONTE_UI.TABELA;
                const pct = economiaPct(c.vrBaseline, c.vrFrete);
                return (
                  <button key={chaveCandidata(c)} type="button" role="option" aria-selected={Boolean(sel)} className={`bf-cand-opcao${sel ? " is-selected" : ""}`} onClick={() => mudar({ candidata: c })}>
                    <span className="bf-cand-opcao-pos">{rankings.length === 1 && c.posicao != null ? `${c.posicao}º` : `${i + 1}.`}</span>
                    <span style={{ flex: 1 }}>
                      <strong>{nomeCandidata(c)}</strong> <span className={`lf-badge lf-badge-${fonte.tone}`}>{fonte.label}</span> {c.ehIncumbente ? <span className="lf-badge lf-badge-info">incumbente</span> : null}{" "}
                      {c.coberturaParcial ? <span className="lf-badge lf-badge-warning">cobertura parcial</span> : null}
                      <div className="lf-muted">
                        {c.tabela || (c.idFreteTabela ? `tabela ${c.idFreteTabela}` : "")} · score {c.score != null ? fmtNumero(c.score, 1) : "—"} · cobertura {c.cobertura != null ? fmtPct(Number(c.cobertura)) : "—"}
                      </div>
                    </span>
                    <span className="is-number" style={{ textAlign: "right" }}>
                      {fmtBRL(Number(c.vrFrete))}
                      <div className={pct != null && pct < 0 ? "bf-text-danger" : "bf-text-success"} style={{ fontSize: 11 }}>
                        {pct != null ? fmtPct(pct) : "—"}
                      </div>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {erros.candidata ? <div className="lf-erro-campo">{erros.candidata}</div> : null}
          {form.candidata && form.candidata.fonte === "PROPOSTA" ? (
            <div className="lf-aviso lf-aviso-warning" style={{ marginTop: 8 }}>
              A integração copia só as linhas da proposta com destino nas UFs ganhas. Linhas sem destino não são copiadas
              {form.candidata.linhasSemDestino ? ` (esta proposta tem ${form.candidata.linhasSemDestino})` : ""}.
            </div>
          ) : null}
          {form.candidata && form.candidata.fonte === "CONECTOR" ? (
            <div className="lf-aviso lf-aviso-info" style={{ marginTop: 8 }}>
              Candidata por conector: não há tabela para ativar; a adjudicação fica registrada como regra do BID.
            </div>
          ) : null}

          <div className="bf-grid-2" style={{ marginTop: 10 }}>
            <FormGroup>
              <Label>Papel</Label>
              <Input
                type="select"
                bsSize="sm"
                value={form.papel}
                aria-label="Papel"
                onChange={(e) => {
                  const v = e.target.value;
                  mudar({ papel: v });
                }}
              >
                {Object.keys(PAPEL_UI).map((k) => (
                  <option key={k} value={k}>
                    {PAPEL_UI[k].label}
                  </option>
                ))}
              </Input>
              {erros.papel ? <div className="lf-erro-campo">{erros.papel}</div> : null}
            </FormGroup>
            <FormGroup>
              <Label>Percentual (opcional)</Label>
              <Input
                type="number"
                bsSize="sm"
                min={0}
                max={100}
                step="0.01"
                value={form.percentual}
                aria-label="Percentual"
                onChange={(e) => {
                  const v = e.target.value;
                  mudar({ percentual: v });
                }}
              />
              {erros.percentual ? <div className="lf-erro-campo">{erros.percentual}</div> : null}
            </FormGroup>
            <FormGroup>
              <Label>Vigência de</Label>
              <Input
                type="date"
                bsSize="sm"
                value={form.vigenciaIni}
                onChange={(e) => {
                  const v = e.target.value;
                  mudar({ vigenciaIni: v });
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label>até</Label>
              <Input
                type="date"
                bsSize="sm"
                value={form.vigenciaFim}
                onChange={(e) => {
                  const v = e.target.value;
                  mudar({ vigenciaFim: v });
                }}
              />
              {erros.vigencia ? <div className="lf-erro-campo">{erros.vigencia}</div> : null}
            </FormGroup>
          </div>
          {precisa ? (
            <FormGroup>
              <Label>Justificativa (fora do 1º lugar do ranking) *</Label>
              <Input
                type="textarea"
                rows={2}
                maxLength={500}
                value={form.justificativa}
                aria-label="Justificativa"
                onChange={(e) => {
                  const v = e.target.value;
                  mudar({ justificativa: v });
                }}
              />
              <div className="lf-muted">Mínimo de {JUSTIFICATIVA_MIN} caracteres.</div>
              {erros.justificativa ? <div className="lf-erro-campo">{erros.justificativa}</div> : null}
            </FormGroup>
          ) : null}

          {tabelasImpacto.length ? (
            <div className="lf-aviso lf-aviso-info" data-testid="bf-impacto">
              <strong>Tabelas ativas da incumbente nestas UFs (não são alteradas):</strong>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {tabelasImpacto.map((t) => (
                  <li key={t.idFreteTabela || t.id}>
                    {t.transportadora || t.nomeTransportadora || ""} · {t.nome || `tabela ${t.idFreteTabela}`}
                    {t.ufs ? ` · ${fmtSiglas(t.ufs)}` : ""}
                    {t.dataValFim ? ` · vigente até ${fmtDataISO(t.dataValFim)}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="text-right">
            <Button color="primary" size="sm" onClick={incluir} disabled={Boolean(busy)}>
              {busy === "incluir" ? <Spinner size="sm" /> : "Incluir vencedora"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="lf-aviso lf-aviso-info">Escolha das vencedoras encerrada. A integração ativa as cópias das vencedoras só com as UFs ganhas.</div>
      )}

      <div className="lf-section">
        <div className="lf-section-title">Vencedoras</div>
        {adjudicacoes.length === 0 ? (
          <div className="lf-muted">Nenhuma vencedora ainda.</div>
        ) : (
          <Table className="bf-table" size="sm" responsive>
            <thead className="text-primary">
              <tr>
                <th>Transportadora</th>
                <th>UFs</th>
                <th>Papel</th>
                <th className="is-number">%</th>
                <th>Vigência</th>
                <th>Integração</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {adjudicacoes.map((a) => {
                const papel = PAPEL_UI[a.papel] || { label: a.papel, tone: "neutral" };
                return (
                  <tr key={a.id}>
                    <td>
                      <strong>{nomeCandidata(a)}</strong>
                      <div className="lf-muted">
                        {(FONTE_UI[a.fonte] || {}).label || a.fonte || ""}
                        {a.idFreteTabela ? ` · tabela ${a.idFreteTabela}` : ""}
                      </div>
                      {a.justificativa ? <div className="lf-muted">“{a.justificativa}”</div> : null}
                      {Number(a.linhasSemDestino) > 0 ? <div className="bf-text-warning" style={{ fontSize: 11 }}>{a.linhasSemDestino} linha(s) sem destino não serão copiadas</div> : null}
                    </td>
                    <td>{fmtSiglas(listaSiglas(a.ufs))}</td>
                    <td>
                      <span className={`lf-badge lf-badge-${papel.tone}`}>{papel.label}</span>
                    </td>
                    <td className="is-number">{a.percentual != null && a.percentual !== "" ? `${fmtNumero(a.percentual, 2)}%` : "—"}</td>
                    <td>
                      {fmtDataISO(a.vigenciaIni)} a {fmtDataISO(a.vigenciaFim)}
                    </td>
                    <td>{a.integradaEm ? `ativada${a.idFreteTabelaAtivada ? ` (tabela ${a.idFreteTabelaAtivada})` : ""}` : "—"}</td>
                    <td>
                      {!encerrada ? (
                        <button type="button" className="lf-btn-abrir" disabled={Boolean(busy)} onClick={() => executar(`excluir-${a.id}`, () => excluirAdjudicacao(empresaId, bid.id, a.id), "Não foi possível excluir.", "Vencedora removida.")}>
                          {busy === `excluir-${a.id}` ? <Spinner size="sm" /> : "Excluir"}
                        </button>
                      ) : (
                        <span className="lf-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      {!encerrada ? (
        <div className="bf-acoes">
          <Button color="success" size="sm" disabled={!situacao.completa || Boolean(busy)} title={situacao.completa ? "" : "Toda UF do escopo precisa de vencedora e de uma principal."} onClick={() => setConfirmarEncerrar(true)}>
            Encerrar escolha das vencedoras
          </Button>
          {!situacao.completa ? <span className="lf-muted">Toda UF do escopo precisa de vencedora e de uma principal, com percentuais somando até 100%.</span> : null}
        </div>
      ) : null}
      {confirmarEncerrar ? (
        <div className="lf-aviso lf-aviso-warning">
          Encerrar libera a integração: as propostas vencedoras viram tabelas ativas só com as UFs ganhas. A tabela da incumbente não muda. Continuar?{" "}
          <Button
            color="warning"
            size="sm"
            disabled={Boolean(busy)}
            onClick={async () => {
              const ok = await executar("encerrar", () => encerrarAdjudicacao(empresaId, bid.id), "Não foi possível encerrar a escolha das vencedoras.", "Escolha encerrada. A integração roda em seguida.");
              if (ok) setConfirmarEncerrar(false);
            }}
          >
            {busy === "encerrar" ? <Spinner size="sm" /> : "Confirmar"}
          </Button>{" "}
          <Button color="secondary" size="sm" outline onClick={() => setConfirmarEncerrar(false)}>
            Desistir
          </Button>
        </div>
      ) : null}
    </>
  );
}
